import {
    baseRequest,
    ReactiveFormSupportDataExecutor,
    type BuildTask,
    type ReactiveFormLoadPlan,
    type ReactiveFormSupportDataLoadPhase,
} from '@fitogest/reactive-form-builder/utils';
import type { ReactiveFormSupportDataSource } from '@fitogest/reactive-form-builder/interfaces';
import type { ClienteModel } from 'src/app/models/cliente.model';
import type { ProductosModel } from 'src/app/models/productos/productos.model';
import type { Uso } from 'src/app/models/productos/uso.model';
import type { TratamientosModel } from 'src/app/models/tratamientos/tratamientos.model';
import type { RequestHandler } from 'src/app/service/OffService/request-handler';
import type { TratamientosFormDefinition } from './form-control/form-definition';
import {
    TRATAMIENTOS_FORM_SUPPORT_DATA_KEYS,
    TratamientosFormLoadPlan,
    type TratamientosFormSupportDataKey,
} from './form-control/tratamientos-form-load-plan';

export interface TratamientosFormDataLoaderEndpoints {
    readonly clientesGET: RequestHandler<ClienteModel[]>;
    readonly productosGET: RequestHandler<ProductosModel[]>;
    readonly mgAgentes: RequestHandler<Uso[]>;
    readonly mgUnidadesMedida: RequestHandler<any[]>;
}

export type TratamientosFormDataLoaderPhase = ReactiveFormSupportDataLoadPhase;

export interface TratamientosFormDataLoaderContext {
    readonly form: TratamientosFormDefinition;
    readonly formType: number;
    readonly endpoints: TratamientosFormDataLoaderEndpoints;
    readonly shouldLoadDistinctAgentes: boolean;
    readonly showUnidadesMedidaSIEX: boolean;
    readonly isClientFieldVisible: boolean;
    readonly loadProducts: () => Promise<unknown>;
    readonly setClients: (clients: ClienteModel[]) => void;
    readonly setProducts: (products: ProductosModel[]) => void;
    readonly setDistinctAgentes: (agentes: Uso[], isEnabled: boolean) => void;
    readonly setUnits: (units: unknown[]) => void;
    readonly setLoadingData: (loading: boolean) => void;
    readonly loadPlan?: ReactiveFormLoadPlan | null;
    readonly phase?: TratamientosFormDataLoaderPhase;
    readonly toggleLoading?: boolean;
}

/** Loads Tratamientos support data through the generic phased support-data executor. */
export class TratamientosFormDataLoader {
    public static load(context: TratamientosFormDataLoaderContext): Promise<boolean> {
        return ReactiveFormSupportDataExecutor.load({
            sources: TratamientosFormDataLoader.resolveSupportDataSources(context),
            loadPlan: context.loadPlan ?? null,
            phase: context.phase ?? 'all',
            setLoading: context.setLoadingData,
            toggleLoading: context.toggleLoading !== false,
            createTasks: (executorContext) => TratamientosFormDataLoader.createBuildTasksForSource(context, executorContext.source),
        });
    }

    public static loadCritical(context: TratamientosFormDataLoaderContext): Promise<boolean> {
        return TratamientosFormDataLoader.load({
            ...context,
            phase: 'critical',
        });
    }

    public static loadDeferred(context: TratamientosFormDataLoaderContext): Promise<boolean> {
        return TratamientosFormDataLoader.load({
            ...context,
            phase: 'deferred',
            toggleLoading: false,
        });
    }

    private static resolveSupportDataSources(
        context: TratamientosFormDataLoaderContext,
    ): readonly ReactiveFormSupportDataSource<TratamientosModel>[] {
        const sources = context.form.formFields.supportDataSources ?? [];

        if (sources.length > 0) {
            return sources;
        }

        return TratamientosFormLoadPlan.createSupportDataSources({
            formType: context.formType,
            shouldLoadDistinctAgentes: context.shouldLoadDistinctAgentes,
            showUnidadesMedidaSIEX: context.showUnidadesMedidaSIEX,
            isClientFieldVisible: context.isClientFieldVisible,
        });
    }

    private static createBuildTasksForSource(
        context: TratamientosFormDataLoaderContext,
        source: ReactiveFormSupportDataSource<TratamientosModel>,
    ): BuildTask[] | null {
        if (!TratamientosFormDataLoader.isTratamientosSupportDataKey(source.key)) {
            return null;
        }

        return TratamientosFormDataLoader.createBuildTasksForKey(context, source.key);
    }

    private static createBuildTasksForKey(
        context: TratamientosFormDataLoaderContext,
        key: TratamientosFormSupportDataKey,
    ): BuildTask[] {
        switch (key) {
        case TRATAMIENTOS_FORM_SUPPORT_DATA_KEYS.clientes:
            return [TratamientosFormDataLoader.createClientsTask(context)];
        case TRATAMIENTOS_FORM_SUPPORT_DATA_KEYS.productos:
            return [TratamientosFormDataLoader.createProductsTask(context)];
        case TRATAMIENTOS_FORM_SUPPORT_DATA_KEYS.agentes:
            return [TratamientosFormDataLoader.createDistinctAgentesTask(context)];
        case TRATAMIENTOS_FORM_SUPPORT_DATA_KEYS.unidadesMedida:
            return [TratamientosFormDataLoader.createUnitsTask(context)];
        }
    }

    private static createClientsTask(context: TratamientosFormDataLoaderContext): BuildTask {
        return (resolve: (ok: boolean) => void): void => {
            baseRequest(context.endpoints.clientesGET)
                .then((response) => {
                    context.setClients(TratamientosFormDataLoader.normalizeArray<ClienteModel>(response));
                    resolve(true);
                })
                .catch((error: unknown) => {
                    console.error('No se han podido cargar los clientes de tratamientos.', error);
                    resolve(false);
                });
        };
    }

    private static createProductsTask(context: TratamientosFormDataLoaderContext): BuildTask {
        return (resolve: (ok: boolean) => void): void => {
            context.loadProducts()
                .then((response) => {
                    context.setProducts(TratamientosFormDataLoader.normalizeArray<ProductosModel>(response));
                    resolve(true);
                })
                .catch((error: unknown) => {
                    console.error('No se han podido cargar los productos de tratamientos.', error);
                    resolve(false);
                });
        };
    }

    private static createDistinctAgentesTask(context: TratamientosFormDataLoaderContext): BuildTask {
        return (resolve: (ok: boolean) => void): void => {
            if (!context.shouldLoadDistinctAgentes) {
                context.setDistinctAgentes([], false);
                resolve(true);
                return;
            }

            baseRequest(context.endpoints.mgAgentes, { getall: true })
                .then((response) => {
                    context.setDistinctAgentes(TratamientosFormDataLoader.normalizeArray<Uso>(response), true);
                    resolve(true);
                })
                .catch((error: unknown) => {
                    console.error('No se han podido cargar los agentes de tratamientos.', error);
                    context.setDistinctAgentes([], false);
                    resolve(false);
                });
        };
    }

    private static createUnitsTask(context: TratamientosFormDataLoaderContext): BuildTask {
        return (resolve: (ok: boolean) => void): void => {
            baseRequest(context.endpoints.mgUnidadesMedida)
                .then((response) => {
                    context.setUnits(TratamientosFormDataLoader.normalizeArray<unknown>(response));
                    resolve(true);
                })
                .catch((error: unknown) => {
                    console.error('No se han podido cargar las unidades de medida de tratamientos.', error);
                    context.setUnits([]);
                    resolve(false);
                });
        };
    }

    private static normalizeArray<T>(response: unknown): T[] {
        if (Array.isArray(response)) {
            return response.filter((item): item is T => item instanceof Object);
        }

        if (typeof response !== 'string') {
            return [];
        }

        return TratamientosFormDataLoader.decodeResponseArray<T>(response)
            .filter((item): item is T => item instanceof Object);
    }

    /**
     * Product endpoints are usually decoded by DecodeInterceptor. This fallback keeps
     * stale encoded caches and unregistered endpoints from breaking the dropdowns.
     */
    private static decodeResponseArray<T>(encodedResponse: string): T[] {
        const trimmedResponse = encodedResponse.trim();

        if (trimmedResponse === '') {
            return [];
        }

        const directParsed = TratamientosFormDataLoader.parseJsonArray<T>(trimmedResponse);

        if (directParsed) {
            return directParsed;
        }

        try {
            const decodedResponse = window.atob(trimmedResponse);

            return TratamientosFormDataLoader.parseJsonArray<T>(decodedResponse) ?? [];
        } catch {
            return [];
        }
    }

    private static parseJsonArray<T>(rawValue: string): T[] | null {
        try {
            const parsedValue = JSON.parse(rawValue) as unknown;

            return Array.isArray(parsedValue) ? parsedValue as T[] : null;
        } catch {
            return null;
        }
    }

    private static isTratamientosSupportDataKey(key: string): key is TratamientosFormSupportDataKey {
        return Object.values(TRATAMIENTOS_FORM_SUPPORT_DATA_KEYS).includes(key as TratamientosFormSupportDataKey);
    }
}
